import { useCallback, useEffect, useRef } from 'react';

type FieldValue = string | boolean;

interface DependencyRule {
    type?: string;
    value?: FieldValue | FieldValue[];
    min?: string | number;
    max?: string | number;
    empty?: boolean;
    like?: string;
    pattern?: string;
    modifier?: string;
    sign?: string;
    strict?: boolean;
}

type DependencyConditions = Array<Record<string, DependencyRule>>;

const dependencyShowClassName = 'dependency-show';
const events = ['input', 'change'] as const;

export function useFieldDependencies(refreshKey?: unknown) {
    const cleanupRef = useRef<() => void>(() => undefined);

    const refreshDependencies = useCallback(() => {
        cleanupRef.current();
        cleanupRef.current = initializeFieldDependencies();
    }, []);

    useEffect(() => {
        refreshDependencies();

        return () => {
            cleanupRef.current();
            cleanupRef.current = () => undefined;
        };
    }, [refreshDependencies, refreshKey]);

    return refreshDependencies;
}

export function initializeFieldDependencies(root: ParentNode = document): () => void {
    const cleanupCallbacks: Array<() => void> = [];
    const fields = Array.from(root.querySelectorAll<HTMLElement>('[data-dependency]'));

    fields.forEach((field) => {
        const dependency = field.dataset.dependency;
        if (!dependency) {
            return;
        }

        try {
            const conditions = JSON.parse(dependency) as DependencyConditions;
            applyDependencyState(field, conditions);
            cleanupCallbacks.push(...bindDependencyListeners(field, conditions));
        } catch (e) {
            console.error('[Dependencies] Error parsing conditions for field:', field, e);
        }
    });

    return () => {
        cleanupCallbacks.forEach((cleanup) => cleanup());
    };
}

function bindDependencyListeners(field: HTMLElement, conditions: DependencyConditions): Array<() => void> {
    const cleanupCallbacks: Array<() => void> = [];

    conditions.forEach((rules) => {
        Object.keys(rules).forEach((selector) => {
            const element = document.querySelector(selector);
            if (!element) {
                return;
            }

            events.forEach((event) => {
                const listener = () => applyDependencyState(field, conditions);

                element.addEventListener(event, listener);
                cleanupCallbacks.push(() => element.removeEventListener(event, listener));
            });
        });
    });

    return cleanupCallbacks;
}

function applyDependencyState(field: HTMLElement, conditions: DependencyConditions) {
    if (checkConditions(conditions)) {
        field.classList.add(dependencyShowClassName);
    } else {
        field.classList.remove(dependencyShowClassName);
    }
}

function checkConditions(conditions: DependencyConditions) {
    return conditions.every((conditionObj) => {
        const selectors = Object.keys(conditionObj);

        return selectors.every((selector) => evaluateDependencyRule(selector, conditionObj[selector]));
    });
}

function evaluateDependencyRule(selector: string, condition: DependencyRule) {
    const type = condition.type;
    const currentValue = getValue(selector);

    const checkValue = typeof condition.value === 'undefined' ? false : condition.value;
    let minValue = typeof condition.min === 'undefined' ? false : Number.parseInt(String(condition.min), 10);
    let maxValue = typeof condition.max === 'undefined' ? false : Number.parseInt(String(condition.max), 10);

    const allowEmpty = typeof condition.empty === 'undefined' ? false : condition.empty;
    const isEmpty = !allowEmpty && currentValue.length < 1;

    const likeSelector = typeof condition.like === 'undefined' ? false : condition.like;
    const likeSelectorValue = getValue(likeSelector);

    const regExpPattern = typeof condition.pattern === 'undefined' ? false : condition.pattern;
    const regExpModifier = typeof condition.modifier === 'undefined' ? 'gi' : condition.modifier;
    const sign = typeof condition.sign === 'undefined' ? false : condition.sign;
    const strict = typeof condition.strict === 'undefined' ? false : condition.strict;

    const emptyTypes = ['empty', 'blank'];
    const notEmptyTypes = ['!empty', 'notEmpty', 'not-empty', 'notempty'];

    const equalTypes = ['equal', '=', '==', '==='];
    const notEqualTypes = ['!equal', '!=', '!==', '!===', 'notEqual', 'not-equal', 'notequal'];

    const regularExpressionTypes = ['regexp', 'exp', 'expression', 'match'];

    if (type && emptyTypes.includes(type)) {
        return currentValue.length < 1;
    }

    if (type && notEmptyTypes.includes(type)) {
        return currentValue.length > 0;
    }

    if (type && equalTypes.includes(type)) {
        if (isEmpty) {
            return false;
        }

        if (likeSelector) {
            return strict
                ? likeSelectorValue.every((value) => currentValue.includes(value))
                : likeSelectorValue.some((value) => currentValue.includes(value));
        }

        if (strict) {
            if (checkValue && Array.isArray(checkValue)) {
                return checkValue.every((value) => currentValue.includes(value));
            }

            if (checkValue && !Array.isArray(checkValue)) {
                return currentValue.includes(checkValue);
            }
        } else {
            if (checkValue && Array.isArray(checkValue)) {
                return checkValue.some((value) => currentValue.includes(value));
            }

            if (checkValue && !Array.isArray(checkValue)) {
                return currentValue.includes(checkValue);
            }
        }
    }

    if (type && notEqualTypes.includes(type)) {
        if (isEmpty) {
            return false;
        }

        if (likeSelector) {
            return strict
                ? likeSelectorValue.every((value) => !currentValue.includes(value))
                : likeSelectorValue.some((value) => !currentValue.includes(value));
        }

        if (strict) {
            if (checkValue && Array.isArray(checkValue)) {
                return checkValue.every((value) => !currentValue.includes(value));
            }

            if (checkValue && !Array.isArray(checkValue)) {
                return !currentValue.includes(checkValue);
            }
        } else {
            if (checkValue && Array.isArray(checkValue)) {
                return checkValue.some((value) => !currentValue.includes(value));
            }

            if (checkValue && !Array.isArray(checkValue)) {
                return !currentValue.includes(checkValue);
            }
        }
    }

    if (type && regularExpressionTypes.includes(type) && regExpPattern) {
        if (isEmpty) {
            return false;
        }

        const exp = new RegExp(regExpPattern, regExpModifier);
        return currentValue.every((value) => exp.test(String(value)));
    }

    if (type === 'length') {
        if (isEmpty) {
            return false;
        }

        if (checkValue && Array.isArray(checkValue)) {
            minValue = Number.parseInt(String(checkValue[0]), 10);
            maxValue = typeof checkValue[1] === 'undefined' ? false : Number.parseInt(String(checkValue[1]), 10);
        }

        if (checkValue && !Array.isArray(checkValue)) {
            minValue = Number.parseInt(String(checkValue), 10);
            maxValue = false;
        }

        const minBoundary = minValue === false ? 0 : minValue;
        const maxBoundary = maxValue === false ? 0 : maxValue;

        return currentValue.every((value) => {
            const valueLength = String(value).length;

            if (!maxValue) {
                return valueLength >= minBoundary;
            }

            if (!minValue) {
                return valueLength <= maxBoundary;
            }

            return valueLength >= minBoundary && valueLength <= maxBoundary;
        });
    }

    if (type === 'range') {
        if (isEmpty) {
            return false;
        }

        if (checkValue && Array.isArray(checkValue)) {
            minValue = Number.parseInt(String(checkValue[0]), 10);
            maxValue = typeof checkValue[1] === 'undefined' ? false : Number.parseInt(String(checkValue[1]), 10);
        }

        const minBoundary = minValue === false ? 0 : minValue;
        const maxBoundary = maxValue === false ? 0 : maxValue;

        return currentValue.every((value) => {
            const numericValue = Number.parseInt(String(value), 10);

            if (!maxValue) {
                return numericValue > minBoundary;
            }

            if (!minValue) {
                return numericValue < maxBoundary;
            }

            return numericValue > minBoundary && numericValue < maxBoundary;
        });
    }

    if (type === 'compare' && sign && checkValue) {
        if (isEmpty) {
            return false;
        }

        const numericCheckValue = Number.parseInt(String(checkValue), 10);

        switch (sign) {
            case '<':
                return currentValue.every((value) => Number.parseInt(String(value), 10) < numericCheckValue);
            case '<=':
                return currentValue.every((value) => Number.parseInt(String(value), 10) <= numericCheckValue);
            case '>':
                return currentValue.every((value) => Number.parseInt(String(value), 10) > numericCheckValue);
            case '>=':
                return currentValue.every((value) => Number.parseInt(String(value), 10) >= numericCheckValue);
            case '=':
            case '==':
                return currentValue.every((value) => Number.parseInt(String(value), 10) === numericCheckValue);
        }
    }

    return false;
}

function getValue(selector: string | false): FieldValue[] {
    const values: FieldValue[] = [];

    if (selector && document.querySelector(selector)) {
        const inputType = document.querySelector(selector)!.tagName.toLowerCase();
        let currentSelector = selector;

        if (inputType === 'crowdin-select') {
            currentSelector = `${selector} option:checked`;
        }

        if (inputType === 'crowdin-checkbox') {
            currentSelector = `${selector}:not(input)`;
        }

        document.querySelectorAll<HTMLInputElement>(currentSelector).forEach((element) => {
            const value = element.value || element.checked;
            values.push(value);
        });
    }

    return values.filter((value) => value !== '');
}
